"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GlassIcon, type GlassIconColor } from "@/components/ui/glass-icon";
import { useCurrentOrgMember } from "@/hooks/useCurrentOrgMember";
import { useOrganizations } from "@/hooks/useOrganizations";
import { getOrgPermissions } from "@/lib/orgs";
import { toast } from "@/lib/toast";
import { usePathname, useRouter } from "next/navigation";
import { IconMediaStopOutline18, IconMicrophoneOutline18, IconRouteOpenOutline18, IconShieldCheckOutline18 } from "nucleo-ui-outline-18";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { createVoiceControlController, GhostCursorOverlay, useGhostCursor, useVoiceControl, type UseVoiceControlReturn, type VoiceControlError } from "realtime-voice-component";
import { createB3OSVoiceTools, type VoicePendingAction, type VoicePendingConfirmation } from "./b3osVoiceTools";
import { VOICE_ROUTES } from "./voiceCapabilities";
const SESSION_ENDPOINT = "/api/openai/realtime/session";
type VoiceNoticeTone = "error" | "info" | "success";
type VoiceIntroItem = {
  color: GlassIconColor;
  detail: string;
  icon: typeof IconMicrophoneOutline18;
  title: string;
};
type B3OSVoiceContextValue = {
  activity: UseVoiceControlReturn["activity"];
  connected: boolean;
  disconnect: () => void;
  isActive: boolean;
  isConnecting: boolean;
  isError: boolean;
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  latestToolCall: UseVoiceControlReturn["latestToolCall"];
  openIntro: () => void;
  requestResponse: () => void;
  speechAnalyser: AnalyserNode | null;
  startVoiceMode: () => Promise<boolean>;
  status: UseVoiceControlReturn["status"];
  transcript: string;
};
const B3OSVoiceContext = createContext<B3OSVoiceContextValue | null>(null);
const VOICE_INTRO_ITEMS: VoiceIntroItem[] = [{
  color: "green",
  detail: "Your browser asks before using your mic.",
  icon: IconShieldCheckOutline18,
  title: "Permission first"
}, {
  color: "blue",
  detail: "Audio is processed only while active.",
  icon: IconMicrophoneOutline18,
  title: "Active session only"
}, {
  color: "zinc",
  detail: "Stop or retry from the rail.",
  icon: IconMediaStopOutline18,
  title: "Rail controls always here"
}];
function asMediaStreamTrack(value: unknown): MediaStreamTrack | null {
  if (typeof MediaStreamTrack === "undefined") return null;
  return value instanceof MediaStreamTrack ? value : null;
}
function normalizeVoiceError(error: VoiceControlError) {
  switch (error.code) {
    case "permission_denied":
      return "Microphone access is blocked. Allow microphone access for this site and try again.";
    case "device_unavailable":
      return "No usable microphone was found.";
    case "insecure_context":
      return "Caddie Copilot requires HTTPS or localhost.";
    case "network_error":
      return "Could not connect to Caddie Copilot.";
    case "unsupported_browser":
      return "This browser does not support Caddie Copilot.";
    default:
      return error.message;
  }
}
function buildVoiceInstructions() {
  const routeList = VOICE_ROUTES.map(route => `${route.id}: ${route.label}`).join(", ");
  return ["You are Caddie, a voice controller for the B3OS dashboard.", "Use the registered tools to operate the visible app. Prefer tools over chat.", "For requests to build, automate, create, or draft an onchain workflow, call submit_dashboard_prompt with the user's request.", "For requests to edit or stage the dashboard prompt without sending it, call set_dashboard_prompt.", "For navigation, call navigate_b3os_route with one of these route ids: " + routeList + ".", "If the user asks what is on screen, what you can do, or which platform actions are available, call get_b3os_context first.", "If a tool returns needsConfirmation, ask the user for a brief yes/no confirmation. If they agree verbally, call confirm_voice_action. If they decline, call cancel_voice_action. The user may also click the confirmation modal.", "Never call confirm_voice_action unless the user has explicitly acknowledged the pending action.", "Do not claim a blockchain transaction, workflow run, billing change, order action, or external action completed unless the UI tool actually performed it.", "Keep any spoken or text response brief."].join("\n");
}
function createInitialController() {
  return createVoiceControlController({
    activationMode: "vad",
    auth: {
      sessionEndpoint: SESSION_ENDPOINT
    },
    instructions: buildVoiceInstructions(),
    maxToolCallHistory: 40,
    model: "gpt-realtime",
    outputMode: "tool-only",
    tools: []
  });
}
export function CaddieCopilotIntroContent(t0) {
  const $ = _c(27);
  const {
    connected,
    connecting,
    onBack,
    onCancel,
    onStart
  } = t0;
  const t1 = connected ? IconRouteOpenOutline18 : IconMicrophoneOutline18;
  let t2;
  if ($[0] !== t1) {
    t2 = <GlassIcon icon={t1} color="blue" size="lg" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = connected ? "Caddie Copilot is active" : "Start Caddie Copilot";
  let t4;
  if ($[2] !== t3) {
    t4 = <DialogTitle className="pt-2">{t3}</DialogTitle>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = connected ? "Caddie Copilot is listening and can use app tools while this session stays active." : "Caddie Copilot listens while active and can navigate, fill prompts, and submit workflow requests.";
  let t6;
  if ($[4] !== t5) {
    t6 = <DialogDescription>{t5}</DialogDescription>;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t2 || $[7] !== t4 || $[8] !== t6) {
    t7 = <DialogHeader>{t2}{t4}{t6}</DialogHeader>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="space-y-5">{VOICE_INTRO_ITEMS.map(_temp)}</div>;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== onBack) {
    t9 = onBack ? <Button type="button" variant="ghost" size="md" onClick={onBack} className="mr-auto">Back</Button> : null;
    $[11] = onBack;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = connected ? "Done" : "Not now";
  let t11;
  if ($[13] !== onCancel || $[14] !== t10) {
    t11 = <Button type="button" variant="ghost" size="md" onClick={onCancel}>{t10}</Button>;
    $[13] = onCancel;
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== connected || $[17] !== connecting || $[18] !== onStart) {
    t12 = !connected ? <Button type="button" variant="brand" size="lg" onClick={() => void onStart()} disabled={connecting}>{connecting ? <><span className="size-2 animate-pulse rounded-full bg-current" />Connecting</> : <><IconMicrophoneOutline18 className="size-4" />Allow and start</>}</Button> : null;
    $[16] = connected;
    $[17] = connecting;
    $[18] = onStart;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t9) {
    t13 = <DialogFooter className="gap-2 sm:items-center">{t9}{t11}{t12}</DialogFooter>;
    $[20] = t11;
    $[21] = t12;
    $[22] = t9;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t7) {
    t14 = <>{t7}{t8}{t13}</>;
    $[24] = t13;
    $[25] = t7;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
}
function _temp(item) {
  return <div key={item.title} className="flex gap-4"><GlassIcon icon={item.icon} color={item.color} size="lg" className="mt-0.5" /><div className="min-w-0"><p className="text-ds-text-primary text-base font-semibold">{item.title}</p><p className="text-ds-text-secondary mt-1 text-sm leading-5">{item.detail}</p></div></div>;
}
function VoiceIntroDialog(t0) {
  const $ = _c(11);
  const {
    connected,
    connecting,
    onOpenChange,
    onStart,
    open
  } = t0;
  let t1;
  if ($[0] !== onOpenChange) {
    t1 = () => onOpenChange(false);
    $[0] = onOpenChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== connected || $[3] !== connecting || $[4] !== onStart || $[5] !== t1) {
    t2 = <DialogContent className="max-w-md" innerClassName="gap-6"><CaddieCopilotIntroContent connected={connected} connecting={connecting} onCancel={t1} onStart={onStart} /></DialogContent>;
    $[2] = connected;
    $[3] = connecting;
    $[4] = onStart;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== onOpenChange || $[8] !== open || $[9] !== t2) {
    t3 = <Dialog open={open} onOpenChange={onOpenChange}>{t2}</Dialog>;
    $[7] = onOpenChange;
    $[8] = open;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function VoiceConfirmationDialog(t0) {
  const $ = _c(30);
  const {
    confirming,
    onCancel,
    onConfirm,
    pending
  } = t0;
  const t1 = Boolean(pending);
  let t2;
  if ($[0] !== onCancel) {
    t2 = open => open ? undefined : onCancel();
    $[0] = onCancel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GlassIcon icon={IconShieldCheckOutline18} color="green" size="lg" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = pending?.title ?? "Confirm action";
  let t5;
  if ($[3] !== t4) {
    t5 = <DialogTitle className="pt-2">{t4}</DialogTitle>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = pending ? "Caddie needs your confirmation before performing this action." : "Caddie needs your confirmation.";
  let t7;
  if ($[5] !== t6) {
    t7 = <DialogDescription>{t6}</DialogDescription>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t5 || $[8] !== t7) {
    t8 = <DialogHeader>{t3}{t5}{t7}</DialogHeader>;
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== pending) {
    t9 = pending ? <div className="border-ds-border-default bg-ds-surface-card rounded-lg border p-4"><p className="text-ds-text-primary text-sm font-semibold">{pending.description}</p><p className="text-ds-text-secondary mt-2 text-xs uppercase">Risk: {pending.risk}</p></div> : null;
    $[10] = pending;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== confirming || $[13] !== onCancel) {
    t10 = <Button type="button" variant="ghost" size="md" onClick={onCancel} disabled={confirming}>Cancel</Button>;
    $[12] = confirming;
    $[13] = onCancel;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = confirming ? "Confirming" : "Confirm";
  let t12;
  if ($[15] !== confirming || $[16] !== onConfirm || $[17] !== t11) {
    t12 = <Button type="button" variant="brand" size="md" onClick={onConfirm} disabled={confirming}>{t11}</Button>;
    $[15] = confirming;
    $[16] = onConfirm;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t10 || $[20] !== t12) {
    t13 = <DialogFooter className="gap-2">{t10}{t12}</DialogFooter>;
    $[19] = t10;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t8 || $[24] !== t9) {
    t14 = <DialogContent className="max-w-md" innerClassName="gap-6">{t8}{t9}{t13}</DialogContent>;
    $[22] = t13;
    $[23] = t8;
    $[24] = t9;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t1 || $[27] !== t14 || $[28] !== t2) {
    t15 = <Dialog open={t1} onOpenChange={t2}>{t14}</Dialog>;
    $[26] = t1;
    $[27] = t14;
    $[28] = t2;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
export function useB3OSVoiceControl() {
  return useContext(B3OSVoiceContext);
}
export function B3OSVoiceProvider({
  children
}: PropsWithChildren) {
  const router = useRouter();
  const pathname = usePathname();
  const {
    organizations,
    selectedOrgId
  } = useOrganizations();
  const {
    cursorState,
    run
  } = useGhostCursor();
  const [controller] = useState(createInitialController);
  const runtime = useVoiceControl(controller);
  const [introOpen, setIntroOpen] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<VoicePendingConfirmation | null>(null);
  const [actionHistory, setActionHistory] = useState<VoicePendingConfirmation[]>([]);
  const [confirmingVoiceAction, setConfirmingVoiceAction] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechAnalyser, setSpeechAnalyser] = useState<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const pendingActionsRef = useRef(new Map<string, VoicePendingAction>());
  const pendingConfirmationRef = useRef<VoicePendingConfirmation | null>(null);
  const actionHistoryRef = useRef<VoicePendingConfirmation[]>([]);
  const {
    member: currentOrgMember
  } = useCurrentOrgMember();
  const selectedOrgName = useMemo(() => organizations.find(org => org.id === selectedOrgId)?.name ?? null, [organizations, selectedOrgId]);
  const currentOrgPermissions = useMemo(() => getOrgPermissions(currentOrgMember), [currentOrgMember]);
  useEffect(() => {
    pendingConfirmationRef.current = pendingConfirmation;
  }, [pendingConfirmation]);
  useEffect(() => {
    actionHistoryRef.current = actionHistory;
  }, [actionHistory]);
  const getPendingConfirmation = useCallback(() => pendingConfirmationRef.current, []);
  const getActionHistory = useCallback(() => actionHistoryRef.current, []);
  const pushNotice = useCallback((tone: VoiceNoticeTone, title: string, detail?: string) => {
    const options = {
      className: "b3os-caddie-toast",
      description: detail,
      duration: tone === "error" ? 7000 : 4500,
      position: "top-right" as const
    };
    if (tone === "error") {
      toast.error(title, options);
      return;
    }
    if (tone === "success") {
      toast.success(title, options);
      return;
    }
    toast.info(title, options);
  }, []);
  const requestConfirmation = useCallback((request: Omit<VoicePendingAction, "createdAt" | "token">): VoicePendingConfirmation => {
    const token = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    const pending: VoicePendingAction = {
      ...request,
      createdAt: Date.now(),
      token
    };
    const publicPending: VoicePendingConfirmation = {
      action: pending.action,
      createdAt: pending.createdAt,
      description: pending.description,
      details: pending.details,
      risk: pending.risk,
      title: pending.title,
      token: pending.token
    };
    pendingActionsRef.current.clear();
    pendingActionsRef.current.set(token, pending);
    pendingConfirmationRef.current = publicPending;
    setPendingConfirmation(publicPending);
    pushNotice("info", "Confirmation needed", request.description);
    return publicPending;
  }, [pushNotice]);
  const cancelPendingAction = useCallback((token_0?: string): VoicePendingConfirmation | null => {
    const targetToken = token_0 ?? pendingConfirmationRef.current?.token;
    if (!targetToken) return null;
    const pending_0 = pendingActionsRef.current.get(targetToken);
    if (!pending_0) return null;
    pendingActionsRef.current.delete(targetToken);
    if (pendingConfirmationRef.current?.token === targetToken) {
      pendingConfirmationRef.current = null;
    }
    setPendingConfirmation(current => current?.token === targetToken ? null : current);
    pushNotice("info", "Voice action cancelled", pending_0.description);
    return {
      action: pending_0.action,
      createdAt: pending_0.createdAt,
      description: pending_0.description,
      details: pending_0.details,
      risk: pending_0.risk,
      title: pending_0.title,
      token: pending_0.token
    };
  }, [pushNotice]);
  const confirmPendingAction = useCallback(async (token_1?: string) => {
    const targetToken_0 = token_1 ?? pendingConfirmationRef.current?.token;
    if (!targetToken_0) {
      return {
        ok: false,
        reason: "No pending voice action to confirm."
      };
    }
    const pending_1 = pendingActionsRef.current.get(targetToken_0);
    if (!pending_1) {
      return {
        ok: false,
        reason: "No pending voice action matched that token."
      };
    }
    const publicPending_0: VoicePendingConfirmation = {
      action: pending_1.action,
      createdAt: pending_1.createdAt,
      description: pending_1.description,
      details: pending_1.details,
      risk: pending_1.risk,
      title: pending_1.title,
      token: pending_1.token
    };
    setConfirmingVoiceAction(true);
    try {
      const result = await pending_1.execute();
      pendingActionsRef.current.delete(targetToken_0);
      if (pendingConfirmationRef.current?.token === targetToken_0) {
        pendingConfirmationRef.current = null;
      }
      setPendingConfirmation(current_0 => current_0?.token === targetToken_0 ? null : current_0);
      setActionHistory(history => {
        const nextHistory = [publicPending_0, ...history].slice(0, 20);
        actionHistoryRef.current = nextHistory;
        return nextHistory;
      });
      pushNotice("success", pending_1.successMessage ?? "Voice action completed");
      return {
        ok: true,
        confirmed: publicPending_0,
        result
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Voice action failed.";
      pushNotice("error", "Voice action failed", message);
      return {
        ok: false,
        confirmed: publicPending_0,
        reason: message
      };
    } finally {
      setConfirmingVoiceAction(false);
    }
  }, [pushNotice]);
  const tools = useMemo(() => createB3OSVoiceTools({
    cancelPendingAction,
    confirmPendingAction,
    currentOrgPermissions,
    currentOrgRole: currentOrgMember?.role ?? null,
    getActionHistory,
    getPendingConfirmation,
    pathname,
    pushNotice,
    requestConfirmation,
    router,
    run,
    selectedOrgId,
    selectedOrgName
  }), [cancelPendingAction, confirmPendingAction, currentOrgMember?.role, currentOrgPermissions, getActionHistory, getPendingConfirmation, pathname, pushNotice, requestConfirmation, router, run, selectedOrgId, selectedOrgName]);
  const teardownSpeechAnalyser = useCallback(() => {
    try {
      sourceRef.current?.disconnect();
    } catch {
      // Ignore disconnect races while the browser is tearing down capture.
    }
    sourceRef.current = null;
    const audioContext = audioContextRef.current;
    audioContextRef.current = null;
    void audioContext?.close().catch(() => undefined);
  }, []);
  const clearSpeechAnalyser = useCallback(() => {
    teardownSpeechAnalyser();
    setSpeechAnalyser(null);
  }, [teardownSpeechAnalyser]);
  const connectSpeechAnalyser = useCallback((localAudioTrack: MediaStreamTrack | null | undefined) => {
    teardownSpeechAnalyser();
    if (!localAudioTrack) {
      setSpeechAnalyser(null);
      return;
    }
    const AudioContextCtor = window.AudioContext || (window as unknown as {
      webkitAudioContext?: typeof AudioContext;
    }).webkitAudioContext;
    if (!AudioContextCtor) {
      setSpeechAnalyser(null);
      return;
    }
    try {
      const audioContext_0 = new AudioContextCtor();
      const source = audioContext_0.createMediaStreamSource(new MediaStream([localAudioTrack]));
      const analyser = audioContext_0.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.72;
      source.connect(analyser);
      audioContextRef.current = audioContext_0;
      sourceRef.current = source;
      setSpeechAnalyser(analyser);
    } catch {
      teardownSpeechAnalyser();
      setSpeechAnalyser(null);
    }
  }, [teardownSpeechAnalyser]);
  useEffect(() => {
    controller.configure({
      activationMode: "vad",
      audio: {
        input: {
          noiseReduction: {
            type: "near_field"
          },
          transcription: {
            model: "gpt-4o-mini-transcribe"
          }
        },
        output: {
          voice: "marin"
        }
      },
      auth: {
        sessionEndpoint: SESSION_ENDPOINT
      },
      instructions: buildVoiceInstructions(),
      maxToolCallHistory: 40,
      model: "gpt-realtime",
      onEvent: event => {
        if (event.type === "voice.transport.connected") {
          connectSpeechAnalyser(asMediaStreamTrack(event.localAudioTrack));
        }
        if (event.type === "input_audio_buffer.speech_started") {
          setIsSpeaking(true);
        }
        if (event.type === "input_audio_buffer.speech_stopped" || event.type === "response.created" || event.type === "voice.transport.disconnected") {
          setIsSpeaking(false);
        }
        if (event.type === "voice.transport.disconnected") {
          clearSpeechAnalyser();
        }
      },
      onError: error_0 => {
        pushNotice("error", "Caddie Copilot error", normalizeVoiceError(error_0));
      },
      outputMode: "tool-only",
      postToolResponse: true,
      tools
    });
  }, [clearSpeechAnalyser, connectSpeechAnalyser, controller, pushNotice, tools]);
  useEffect(() => {
    return () => {
      teardownSpeechAnalyser();
      controller.destroy();
    };
  }, [controller, teardownSpeechAnalyser]);
  const startVoiceMode = useCallback(async () => {
    try {
      await runtime.connect();
      setIntroOpen(false);
      pushNotice("success", "Caddie Copilot is listening", "You can stop anytime with the button on the right.");
      return true;
    } catch (error_1) {
      const message_0 = error_1 instanceof Error ? error_1.message : "Could not start Caddie Copilot.";
      pushNotice("error", "Could not start Caddie Copilot", message_0);
      return false;
    }
  }, [pushNotice, runtime]);
  const disconnect = useCallback(() => {
    runtime.disconnect();
    setIsSpeaking(false);
    clearSpeechAnalyser();
    pushNotice("info", "Caddie Copilot stopped");
  }, [clearSpeechAnalyser, pushNotice, runtime]);
  const contextValue = useMemo<B3OSVoiceContextValue>(() => ({
    activity: runtime.activity,
    connected: runtime.connected,
    disconnect,
    isActive: runtime.connected || runtime.status === "connecting" || runtime.status === "error",
    isConnecting: runtime.status === "connecting",
    isError: runtime.status === "error",
    isListening: runtime.status === "listening",
    isProcessing: runtime.status === "processing" || runtime.activity === "executing",
    isSpeaking,
    latestToolCall: runtime.latestToolCall,
    openIntro: () => setIntroOpen(true),
    requestResponse: runtime.requestResponse,
    speechAnalyser,
    startVoiceMode,
    status: runtime.status,
    transcript: runtime.transcript
  }), [disconnect, isSpeaking, runtime, speechAnalyser, startVoiceMode]);
  return <B3OSVoiceContext.Provider value={contextValue}>
      {children}
      <GhostCursorOverlay avatarAlt="Caddie" avatarSrc="/caddie/caddie-default-cropped.svg" className="text-ds-brand-600 dark:text-blue-300" state={cursorState} />
      <VoiceIntroDialog connected={runtime.connected} connecting={runtime.status === "connecting"} onOpenChange={setIntroOpen} onStart={startVoiceMode} open={introOpen} />
      <VoiceConfirmationDialog confirming={confirmingVoiceAction} onCancel={() => {
      cancelPendingAction();
    }} onConfirm={() => {
      void confirmPendingAction();
    }} pending={pendingConfirmation} />
    </B3OSVoiceContext.Provider>;
}